import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';
import { formatMoneyCents } from '@theme/utilities';
import { addToCart } from './cart-add-helper.js';

class CapillairePdp extends Component {
  requiredRefs = [
    'priceContainer',
    'variantIdInput',
    'sellingPlanInput',
    'addToCartButton',
    'addToCartError',
    'liveRegion',
    'backInStockCapture',
    'backInStockForm',
    'backInStockEmail',
    'backInStockConsent',
    'backInStockSubmit',
    'backInStockStatus',
    'backInStockSelection',
  ];

  #originalButtonText = '';
  #originalSlideImg = null;
  #originalSlideSrc = '';
  #originalSlideSrcset = '';
  #originalThumbImg = null;
  #originalThumbSrc = '';
  #originalThumbSrcset = '';
  #originalPriceClassName = '';
  #originalDocumentTitle = '';
  #documentTitleSuffix = '';
  #backInStockSelection = null;
  #section = null;
  #tierPriceBaseState = new WeakMap();
  #tierPriceSubscriptionState = new WeakSet();
  #handleVariantSelected = () => this.handleVariantSelected();
  #handleVariantUpdate = (event) => this.handleVariantUpdate(event);
  #handleStickyAtcVisibility = () => this.#updateStickyAtcVisibility();
  #mobileStickyMedia = window.matchMedia('(max-width: 989px)');

  connectedCallback() {
    super.connectedCallback();

    this.selectedTierIndex = 0;
    this.#hydrateFromDom({ resetSelection: true });

    // Variant events (for format changes on self tier)
    const section = this.closest('.shopify-section');
    if (section) {
      this.#section = section;
      this.#section.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
      this.#section.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    }

    window.addEventListener('scroll', this.#handleStickyAtcVisibility, { passive: true });
    window.addEventListener('resize', this.#handleStickyAtcVisibility);
    document.body.classList.add('capillaire-pdp-page');
    this.#addMobileStickyMediaListener();
    this.#updateStickyAtcVisibility();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#hydrateFromDom({ resetSelection: true });
  }

  disconnectedCallback() {
    if (this.#section) {
      this.#section.removeEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
      this.#section.removeEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
      this.#section = null;
    }

    window.removeEventListener('scroll', this.#handleStickyAtcVisibility);
    window.removeEventListener('resize', this.#handleStickyAtcVisibility);
    this.#removeMobileStickyMediaListener();
    document.body.classList.remove('capillaire-pdp-page');

    super.disconnectedCallback();
  }

  #addMobileStickyMediaListener() {
    if (typeof this.#mobileStickyMedia.addEventListener === 'function') {
      this.#mobileStickyMedia.addEventListener('change', this.#handleStickyAtcVisibility);
      return;
    }

    if (typeof this.#mobileStickyMedia.addListener === 'function') {
      this.#mobileStickyMedia.addListener(this.#handleStickyAtcVisibility);
    }
  }

  #removeMobileStickyMediaListener() {
    if (typeof this.#mobileStickyMedia.removeEventListener === 'function') {
      this.#mobileStickyMedia.removeEventListener('change', this.#handleStickyAtcVisibility);
      return;
    }

    if (typeof this.#mobileStickyMedia.removeListener === 'function') {
      this.#mobileStickyMedia.removeListener(this.#handleStickyAtcVisibility);
    }
  }

  #hydrateFromDom({ resetSelection = false } = {}) {
    this.productId = this.dataset.productId;
    this.originalPriceHtml = this.refs.priceContainer?.innerHTML || this.originalPriceHtml || '';
    this.#originalPriceClassName = this.refs.priceContainer?.className || this.#originalPriceClassName || '';
    this.#captureDocumentTitlePattern();

    // Cache original first slide and thumbnail images for tier swapping
    this.#cacheOriginalMedia();

    this.tiers = [];
    this.tierDiscount = 25;
    this.hasUpsellTiers = false;
    this.hasFormatChoice = this.dataset.hasFormatChoice === 'true';

    if (this.refs.tierData) {
      try {
        const data = JSON.parse(this.refs.tierData.textContent);
        const discount = Number(data.discount);

        this.tiers = data.tiers || [];
        this.tierDiscount = Number.isFinite(discount) ? discount : 25;
        this.hasUpsellTiers = this.tiers.length > 1;
      } catch (e) {
        console.error('Capillaire PDP: failed to parse tier data', e);
      }
    }

    if (resetSelection || this.selectedTierIndex >= this.tiers.length) {
      this.selectedTierIndex = 0;
    }

    if (this.hasUpsellTiers) {
      this.#checkTierRadio(this.selectedTierIndex);
      this.#applyTierSelection(this.selectedTierIndex, false);
    } else {
      this.#setPrimaryButtonText(null, false);
      this.#updateBackInStockCapture(
        !this.refs.addToCartButton?.disabled,
        this.#buildSelfBackInStockSelection()
      );
    }

    this.#syncVariantPickerStateClasses();
    this.#syncStateClasses();
  }

  #checkTierRadio(index) {
    const tierRadios = this.refs.tierRadios;
    if (!tierRadios) return;

    const radios = Array.isArray(tierRadios) ? tierRadios : [tierRadios];
    radios.forEach((radio) => {
      radio.checked = radio.value === String(index);
    });
  }

  /* ── Tier selection ── */

  handleTierChange() {
    const checked = this.refs.tierRadios?.find?.((r) => r.checked) || this.refs.tierRadios;
    if (!checked || !checked.value) return;

    const index = parseInt(checked.value, 10);
    if (index === this.selectedTierIndex) return;

    this.selectedTierIndex = index;
    this.#applyTierSelection(index, true);
  }

  #applyTierSelection(index, animate) {
    let tier = this.tiers[index];
    if (!tier) return;

    this.#checkTierRadio(index);
    if (!this.#syncTierFormatVisibility()) {
      index = 0;
      this.selectedTierIndex = 0;
      this.#checkTierRadio(index);
      tier = this.tiers[index];
      if (!tier) return;
    }

    // Visual state on radio labels
    const options = this.querySelectorAll('.capillaire-pdp__tier-option');
    options.forEach((opt) => {
      const radio = opt.querySelector('input[type="radio"]');
      opt.classList.toggle('capillaire-pdp__tier-option--selected', radio?.value === String(index));
    });

    // Variant ID
    this.refs.variantIdInput.value = tier.variantId;
    this.#setProductIdentity(tier);
    this.#setProductNarrative(tier);
    this.#setProductAccordions(tier);

    // Price display
    let promoPrice = tier.price;
    const promoVisible = !!tier.sameProductBogoEligible && tier.available;
    if (tier.isSelf) {
      this.#restoreOriginalPriceHtml();
    } else if (tier.compareAtPrice && tier.compareAtPrice > 0) {
      const displayPrice = tier.discountPercentage > 0
        ? Math.round(tier.price * (1 - tier.discountPercentage / 100))
        : tier.price;
      promoPrice = displayPrice;
      this.#setPriceHtml(displayPrice, tier.compareAtPrice);
    } else {
      this.#setPriceHtml(tier.price);
    }
    this.#setSameProductPromo(promoPrice, promoVisible);

    // Swap first gallery image
    this.#swapFirstImage(tier.isSelf ? null : tier.image);

    // Format is a top-level purchase decision; unsupported bundles are filtered from the tier list.
    if (this.refs.variantPicker) {
      this.refs.variantPicker.hidden = !(this.hasFormatChoice && tier.available);
    }

    // Subscription checkbox: show only if tier has selling plan, reset on tier change
    if (this.refs.subCheckContainer) {
      const hasPlan = !!tier.sellingPlanId;
      this.refs.subCheckContainer.hidden = !hasPlan;

      if (this.refs.subscriptionCheckbox) {
        this.refs.subscriptionCheckbox.checked = false;
        this.refs.sellingPlanInput.value = '';
      }
      if (this.refs.subInfo) {
        this.refs.subInfo.hidden = true;
      }
      this.#updateTierPrices(false);
    }

    // Add to cart button state
    if (!tier.available) {
      this.refs.addToCartButton.setAttribute('disabled', '');
      this.refs.addToCartButton.textContent = this.dataset.soldOutText;
    } else {
      this.refs.addToCartButton.removeAttribute('disabled');
      this.#setPrimaryButtonText(tier, false);
    }

    this.#updateBackInStockCapture(tier.available, this.#buildTierBackInStockSelection(tier));
    this.#setPurchaseAvailability(tier.available);
    this.#setStickyProductSummary(tier, promoPrice);
    this.#updateStickyAtcVisibility();
  }

  /* ── Subscription checkbox ── */

  handleSubscriptionToggle() {
    const tier = this.tiers[this.selectedTierIndex];
    const isChecked = this.refs.subscriptionCheckbox?.checked;

    this.refs.sellingPlanInput.value = isChecked && tier ? (tier.sellingPlanId || '') : '';

    if (this.refs.subInfo) {
      this.refs.subInfo.hidden = !isChecked;
    }

    this.#setPrimaryButtonText(tier, isChecked);

    // Update main price display with subscription discount
    if (tier) {
      let promoPrice = tier.price;
      const promoVisible = !isChecked && !!tier.sameProductBogoEligible && tier.available;

      if (isChecked) {
        const basePrice = tier.discountPercentage > 0
          ? Math.round(tier.price * (1 - tier.discountPercentage / 100))
          : tier.price;
        const discounted = Math.round(basePrice * (1 - this.tierDiscount / 100));
        const compareAt = tier.compareAtPrice > 0 ? tier.compareAtPrice : tier.price;
        promoPrice = discounted;
        this.#setPriceHtml(discounted, compareAt);
      } else if (tier.isSelf) {
        this.#restoreOriginalPriceHtml();
      } else if (tier.compareAtPrice > 0) {
        const displayPrice = tier.discountPercentage > 0
          ? Math.round(tier.price * (1 - tier.discountPercentage / 100))
          : tier.price;
        promoPrice = displayPrice;
        this.#setPriceHtml(displayPrice, tier.compareAtPrice);
      } else {
        this.#setPriceHtml(tier.price);
      }

      this.#setSameProductPromo(promoPrice, promoVisible);
      this.#setStickyPrice(promoPrice);
    }

    // Update tier selector prices
    this.#updateTierPrices(isChecked);
  }

  #updateTierPrices(showDiscount) {
    const tierPrices = this.refs.tierPrices;
    if (!tierPrices) return;
    const prices = Array.isArray(tierPrices) ? tierPrices : [tierPrices];

    prices.forEach((el, index) => {
      this.#rememberTierPriceBaseState(el);

      const originalPrice = parseInt(el.dataset.price, 10);
      if (!originalPrice) return;

      const tier = this.tiers[index];
      const hasPlan = tier && !!tier.sellingPlanId;

      if (showDiscount && hasPlan) {
        const discounted = Math.round(originalPrice * (1 - this.tierDiscount / 100));
        el.classList.add('capillaire-pdp__tier-price--discounted');
        el.innerHTML =
          `<span class="capillaire-pdp__tier-price-original">${formatMoneyCents(originalPrice)}</span>` +
          `<span class="capillaire-pdp__tier-price-sale">${formatMoneyCents(discounted)}</span>`;
        this.#tierPriceSubscriptionState.add(el);
      } else if (this.#tierPriceSubscriptionState.has(el)) {
        this.#restoreTierPriceBaseState(el);
        this.#tierPriceSubscriptionState.delete(el);
      }
    });
  }

  #rememberTierPriceBaseState(el, force = false) {
    if (!el || (!force && this.#tierPriceBaseState.has(el))) return;

    this.#tierPriceBaseState.set(el, {
      className: el.className,
      html: el.innerHTML,
    });

    if (force) {
      this.#tierPriceSubscriptionState.delete(el);
    }
  }

  #restoreTierPriceBaseState(el) {
    const baseState = this.#tierPriceBaseState.get(el);
    if (!baseState) return;

    el.className = baseState.className;
    el.innerHTML = baseState.html;
  }

  #syncTierFormatVisibility() {
    if (!this.hasFormatChoice) return true;

    const formatKey = this.#currentFormatKey();
    let selectedVisible = true;

    const options = this.querySelectorAll('.capillaire-pdp__tier-option');
    options.forEach((option) => {
      const shouldHide = !this.#tierSupportsCurrentFormat(option, formatKey);
      option.hidden = shouldHide;

      const radio = option.querySelector('input[type="radio"]');
      if (radio) {
        radio.disabled = shouldHide;
        if (radio.checked && shouldHide) selectedVisible = false;
      }
    });

    this.#syncStateClasses();
    return selectedVisible;
  }

  #tierSupportsCurrentFormat(option, formatKey) {
    const compatibility = option.dataset.formatCompatibility || (option.dataset.supportedFormats ? 'specific' : 'none');

    if (compatibility === 'self' || compatibility === 'all') return true;
    if (compatibility !== 'specific') return false;
    if (!formatKey) return true;

    const supportedFormats = option.dataset.supportedFormats || '';
    return supportedFormats.indexOf(`|${formatKey}|`) !== -1;
  }

  #currentFormatKey() {
    const checked = this.refs.variantPicker?.querySelector('input:checked');
    const value = checked?.value || this.dataset.selectedVariantTitle || '';

    return this.#formatKey(value);
  }

  #formatKey(value) {
    return String(value)
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  #restoreOriginalPriceHtml() {
    if (!this.refs.priceContainer) return;

    if (this.#originalPriceClassName) {
      this.refs.priceContainer.className = this.#originalPriceClassName;
    }

    this.refs.priceContainer.innerHTML = this.originalPriceHtml;
  }

  #setPriceHtml(price, compareAt = 0) {
    if (!this.refs.priceContainer) return;

    this.refs.priceContainer.classList.toggle('price-container--sale', compareAt > price);
    this.refs.priceContainer.innerHTML = this.#renderPriceHtml(price, compareAt);
  }

  #renderPriceHtml(price, compareAt = 0) {
    const hasCompare = compareAt > price;
    const savings = hasCompare ? compareAt - price : 0;
    const savingsTemplate = this.dataset.priceSavingsTemplate || 'Save __amount__';

    return `
      <span class="price-current" role="group">
        ${hasCompare ? `<span class="visually-hidden">${this.#escapeHtml(this.dataset.priceSaleLabel || '')}&nbsp;</span>` : ''}
        <span class="price-label price-label--current">${this.#escapeHtml(this.dataset.priceLabel || 'Price')}</span>
        <span class="price">${formatMoneyCents(price)}</span>
      </span>
      ${hasCompare ? `
        <span class="price-compare" role="group">
          <span class="price-label price-label--compare compare-at-price compare-at-price-override compare-at-price-message">${this.#escapeHtml(this.dataset.priceRegularLabel || 'Value')}&nbsp;</span>
          <span class="compare-at-price compare-at-price-override">${formatMoneyCents(compareAt)}</span>
        </span>
        <span class="price-savings">${this.#escapeHtml(savingsTemplate.replace('__amount__', formatMoneyCents(savings)))}</span>
      ` : ''}
    `;
  }

  #setProductNarrative(selection) {
    if (!selection) return;

    const description = typeof selection.description === 'string' ? selection.description.trim() : '';
    const descriptionElement = this.refs.productDescription || this.querySelector('[ref="productDescription"]');
    if (descriptionElement) {
      descriptionElement.textContent = description;
      descriptionElement.hidden = !description;
    }

    const benefits = Array.isArray(selection.benefits)
      ? selection.benefits.map((benefit) => String(benefit || '').trim()).filter(Boolean)
      : [];
    const benefitsElement = this.refs.productBenefits || this.querySelector('[ref="productBenefits"]');
    const benefitsList = this.refs.productBenefitsList || benefitsElement?.querySelector('ul');

    if (benefitsList) {
      benefitsList.innerHTML = benefits
        .map((benefit) => `<li>${this.#escapeHtml(benefit)}</li>`)
        .join('');
    }

    if (benefitsElement) {
      benefitsElement.hidden = benefits.length === 0;
    }
  }

  #setProductAccordions(selection) {
    if (!selection) return;

    this.#setAccordionHtml(
      this.refs.detailsAccordion || this.querySelector('[ref="detailsAccordion"]'),
      this.refs.detailsContent || this.querySelector('[ref="detailsContent"]'),
      selection.detailsHtml
    );

    this.#setAccordionList(
      this.refs.ingredientsAccordion || this.querySelector('[ref="ingredientsAccordion"]'),
      this.refs.ingredientsList || this.querySelector('[ref="ingredientsList"]'),
      selection.ingredients
    );

    this.#setAccordionList(
      this.refs.instructionsAccordion || this.querySelector('[ref="instructionsAccordion"]'),
      this.refs.instructionsList || this.querySelector('[ref="instructionsList"]'),
      selection.instructions
    );
  }

  #setAccordionHtml(accordion, contentElement, html) {
    const content = typeof html === 'string' ? html.trim() : '';

    if (contentElement) {
      contentElement.innerHTML = content;
    }

    this.#setAccordionVisibility(accordion, !!content);
  }

  #setAccordionList(accordion, listElement, items) {
    const listItems = Array.isArray(items)
      ? items.map((item) => String(item || '').trim()).filter(Boolean)
      : [];

    if (listElement) {
      listElement.innerHTML = listItems
        .map((item) => `<li>${this.#escapeHtml(item)}</li>`)
        .join('');
    }

    this.#setAccordionVisibility(accordion, listItems.length > 0);
  }

  #setAccordionVisibility(accordion, isVisible) {
    if (!accordion) return;

    accordion.hidden = !isVisible;
    if (!isVisible) {
      accordion.removeAttribute('open');
    }
  }

  #setSameProductPromo(price, visible = true) {
    if (!this.refs.sameProductPromo) return;

    this.refs.sameProductPromo.hidden = !visible;
    this.#syncStateClasses();

    const discountLabel = this.refs.sameProductPromoDiscount;
    if (!discountLabel || !Number.isFinite(price) || price <= 0) return;

    const template = this.#readDatasetTemplate('sameProductDiscountTemplate');
    discountLabel.textContent = template.replace('__amount__', formatMoneyCents(price));

    const valueLabel = this.refs.sameProductPromoValue;
    if (valueLabel) {
      const valueTemplate = this.#readDatasetTemplate('sameProductValueTemplate');
      valueLabel.textContent = valueTemplate.replace('__value__', formatMoneyCents(price * 2));
    }
  }

  #readDatasetTemplate(key) {
    const value = this.dataset[key] || '';

    try {
      return this.#decodeHtmlEntities(JSON.parse(value));
    } catch (_error) {
      return this.#decodeHtmlEntities(value);
    }
  }

  #decodeHtmlEntities(value) {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = String(value);
    return textarea.value;
  }

  #escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  /* ── Media swap for tier selection ── */

  #cacheOriginalMedia() {
    const firstSlide = this.querySelector('slideshow-slide');
    this.#originalSlideImg = firstSlide?.querySelector('.product-media__image');
    if (this.#originalSlideImg) {
      this.#originalSlideSrc = this.#originalSlideImg.src;
      this.#originalSlideSrcset = this.#originalSlideImg.srcset;
    }
    const firstThumb = this.querySelector('.slideshow-controls__thumbnail img');
    this.#originalThumbImg = firstThumb;
    if (firstThumb) {
      this.#originalThumbSrc = firstThumb.src;
      this.#originalThumbSrcset = firstThumb.srcset;
    }
  }

  #buildSrcset(baseUrl, widths) {
    return widths
      .map((w) => `${baseUrl.replace(/&width=\d+/, `&width=${w}`)} ${w}w`)
      .join(', ');
  }

  #swapFirstImage(tierImageUrl) {
    if (!this.#originalSlideImg) return;
    if (tierImageUrl) {
      const slideSrcset = this.#buildSrcset(tierImageUrl, [400, 600, 800, 1000, 1200]);
      this.#originalSlideImg.srcset = slideSrcset;
      this.#originalSlideImg.src = tierImageUrl;
      if (this.#originalThumbImg) {
        const thumbSrcset = this.#buildSrcset(tierImageUrl, [72, 144]);
        this.#originalThumbImg.srcset = thumbSrcset;
        this.#originalThumbImg.src = tierImageUrl.replace(/&width=\d+/, '&width=144');
      }
    } else {
      this.#originalSlideImg.src = this.#originalSlideSrc;
      this.#originalSlideImg.srcset = this.#originalSlideSrcset;
      if (this.#originalThumbImg) {
        this.#originalThumbImg.src = this.#originalThumbSrc;
        this.#originalThumbImg.srcset = this.#originalThumbSrcset;
      }
    }
  }

  /* ── Variant events (format changes — self tier only) ── */

  handleVariantSelected() {
    this.#syncVariantPickerStateClasses();
    this.refs.addToCartButton.setAttribute('disabled', '');
    this.refs.addToCartButton.textContent = this.dataset.addingText || '...';
    if (this.refs.stickyAddToCartButton) {
      this.refs.stickyAddToCartButton.disabled = true;
    }
    this.#updateStickyAtcVisibility();
  }

  handleVariantUpdate(event) {
    const { resource, data } = event.detail;

    // Keep the tier UI/data aligned with the selected variant metafields from the section render.
    if (data?.html) {
      const newPrice = data.html.getElementById(`price-${this.dataset.sectionId}`);
      if (newPrice) {
        const newPriceContainer = newPrice.querySelector('[ref="priceContainer"]');
        if (newPriceContainer && this.refs.priceContainer) {
          this.refs.priceContainer.className = newPriceContainer.className;
          this.refs.priceContainer.innerHTML = newPriceContainer.innerHTML;
          this.originalPriceHtml = newPriceContainer.innerHTML;
          this.#originalPriceClassName = newPriceContainer.className;
        }
      }

      this.#syncProductStateFromHtml(data.html);
    }

    // Always update self tier data so it stays current
    if (resource && this.tiers.length > 0) {
      this.tiers[0].variantId = resource.id;
      this.tiers[0].variantTitle = resource.title || '';
      this.tiers[0].variantSku = resource.sku || '';
      this.tiers[0].price = resource.price;
      this.tiers[0].available = resource.available;
    }

    if (!this.#syncTierFormatVisibility()) {
      this.selectedTierIndex = 0;
      this.#checkTierRadio(0);
    }
    this.#syncVariantPickerStateClasses();

    // Only update visible UI if self tier is selected
    if (this.selectedTierIndex !== 0) return;

    if (resource) {
      this.refs.variantIdInput.value = resource.id;
    }

    // Button state
    if (!resource || !resource.available) {
      this.refs.addToCartButton.setAttribute('disabled', '');
      this.refs.addToCartButton.textContent = this.dataset.soldOutText;
      this.#updateBackInStockCapture(false, this.#buildSelfBackInStockSelection(resource));
      this.#setPurchaseAvailability(false);
    } else {
      this.refs.addToCartButton.removeAttribute('disabled');
      this.#setPrimaryButtonText(this.tiers[0], false);
      this.#updateBackInStockCapture(true, this.#buildSelfBackInStockSelection(resource));
      this.#setPurchaseAvailability(true);
    }

    // Update self tier price in the radio row (first tierPrices element)
    const tierPrices = this.refs.tierPrices;
    const selfPriceEl = Array.isArray(tierPrices) ? tierPrices[0] : tierPrices;
    if (selfPriceEl && resource) {
      selfPriceEl.dataset.price = resource.price;
      selfPriceEl.textContent = formatMoneyCents(resource.price);
      this.#rememberTierPriceBaseState(selfPriceEl, true);
    }

    if (resource) {
      const selfPromoEligible = this.tiers[0]?.sameProductBogoEligible ?? !!this.refs.sameProductPromo;
      this.#setSameProductPromo(
        resource.price,
        resource.available && selfPromoEligible && !this.refs.subscriptionCheckbox?.checked
      );
      this.#setStickyProductSummary({
        variantTitle: resource.title || '',
        price: resource.price,
        available: resource.available,
      });
    }

    // Navigate slideshow to variant media
    if (resource?.featured_media) {
      this.#navigateToVariantMedia(resource.featured_media.id);
    }
  }

  #syncProductStateFromHtml(html) {
    const newRoot = html.querySelector(`capillaire-pdp[data-section-id="${this.dataset.sectionId}"]`);
    if (!newRoot) return;

    this.#copyRootDataAttributes(newRoot);

    const currentTitle = this.querySelector('.capillaire-pdp__title');
    const newTitle = newRoot.querySelector('.capillaire-pdp__title');
    if (currentTitle && newTitle) {
      morph(currentTitle, newTitle, { childrenOnly: false });
    }

    const currentDescription = this.querySelector('[ref="productDescription"]');
    const newDescription = newRoot.querySelector('[ref="productDescription"]');
    if (currentDescription && newDescription) {
      morph(currentDescription, newDescription, { childrenOnly: false });
    }

    const currentBenefits = this.querySelector('[ref="productBenefits"]');
    const newBenefits = newRoot.querySelector('[ref="productBenefits"]');
    if (currentBenefits && newBenefits) {
      morph(currentBenefits, newBenefits, { childrenOnly: false });
    }

    const newPurchaseConfig = newRoot.querySelector('[ref="purchaseConfig"]');
    if (!newPurchaseConfig || !this.refs.purchaseConfig) return;

    morph(this.refs.purchaseConfig, newPurchaseConfig, { childrenOnly: false });
    this.updatedCallback();
  }

  #copyRootDataAttributes(newRoot) {
    for (const attribute of Array.from(this.attributes)) {
      if (attribute.name.startsWith('data-') && !newRoot.hasAttribute(attribute.name)) {
        this.removeAttribute(attribute.name);
      }
    }

    for (const attribute of Array.from(newRoot.attributes)) {
      if (attribute.name.startsWith('data-')) {
        this.setAttribute(attribute.name, attribute.value);
      }
    }
  }

  #buildSelfBackInStockSelection(resource = null) {
    return {
      productId: this.dataset.productId || '',
      productHandle: this.dataset.productHandle || '',
      productTitle: this.dataset.productTitle || '',
      productUrl: this.dataset.productUrl || window.location.pathname,
      variantId: String(resource?.id || this.refs.variantIdInput.value || ''),
      variantTitle: resource?.title || this.dataset.selectedVariantTitle || '',
      variantSku: resource?.sku || this.dataset.selectedVariantSku || '',
    };
  }

  #buildTierBackInStockSelection(tier) {
    if (!tier || tier.isSelf) {
      return this.#buildSelfBackInStockSelection();
    }

    return {
      productId: String(tier.productId || this.dataset.productId || ''),
      productHandle: tier.productHandle || this.dataset.productHandle || '',
      productTitle: tier.productTitle || this.dataset.productTitle || '',
      productUrl: tier.productUrl || this.dataset.productUrl || window.location.pathname,
      variantId: String(tier.variantId || ''),
      variantTitle: tier.variantTitle || '',
      variantSku: tier.variantSku || '',
    };
  }

  #updateBackInStockCapture(isAvailable, selection) {
    if (!this.refs.backInStockCapture) return;

    this.refs.backInStockCapture.hidden = !!isAvailable;
    this.#backInStockSelection = selection || this.#buildSelfBackInStockSelection();
    this.#updateBackInStockSelectionLabel();
    this.#setBackInStockStatus('', '');
  }

  #setPurchaseAvailability(isAvailable) {
    if (this.refs.purchaseConfig) {
      this.refs.purchaseConfig.classList.toggle('capillaire-pdp__purchase-config--sold-out', !isAvailable);
    }

    if (this.refs.buyRow) {
      this.refs.buyRow.hidden = !isAvailable;
    }

    if (this.refs.tierList) {
      this.refs.tierList.hidden = !this.hasUpsellTiers;
    }

    if (this.refs.tierHeading) {
      this.refs.tierHeading.hidden = !this.hasUpsellTiers;
    }

    if (this.refs.variantPicker) {
      this.refs.variantPicker.hidden = !this.hasFormatChoice;
    }

    if (this.refs.trustSignals) {
      this.refs.trustSignals.hidden = !isAvailable;
    }

    if (this.refs.subCheckContainer) {
      const tier = this.tiers[this.selectedTierIndex];
      this.refs.subCheckContainer.hidden = !isAvailable || !tier?.sellingPlanId;

      if (!isAvailable) {
        if (this.refs.subscriptionCheckbox) {
          this.refs.subscriptionCheckbox.checked = false;
        }
        this.refs.sellingPlanInput.value = '';
        if (this.refs.subInfo) {
          this.refs.subInfo.hidden = true;
        }
        this.#updateTierPrices(false);
      }
    }

    if (this.refs.stickyAddToCartButton) {
      this.refs.stickyAddToCartButton.disabled = !isAvailable;
    }

    this.#syncStateClasses();
    this.#updateStickyAtcVisibility();
  }

  #syncStateClasses() {
    const promoVisible = !!this.refs.sameProductPromo && !this.refs.sameProductPromo.hidden;
    const tiersVisible = !!this.refs.tierList && !this.refs.tierList.hidden;

    this.classList.toggle('capillaire-pdp--same-product-promo-visible', promoVisible);
    this.classList.toggle('capillaire-pdp--has-visible-tiers', tiersVisible);
  }

  #setStickyProductSummary(selection, priceOverride = null) {
    if (!selection) return;

    const productTitle = selection.productTitle || this.dataset.productTitle || '';
    if (this.refs.stickyTitle && productTitle) {
      this.refs.stickyTitle.textContent = productTitle;
    }

    const variant = this.refs.stickyVariant;
    if (variant) {
      const title = selection.variantTitle || '';
      variant.textContent = title === 'Default Title' ? '' : title;
      variant.hidden = !variant.textContent;
    }

    this.#setStickyPrice(Number.isFinite(priceOverride) ? priceOverride : selection.price);
  }

  #setPrimaryButtonText(tier, isSubscription = false) {
    const button = this.refs.addToCartButton;
    if (!button || button.disabled) return;

    const text = isSubscription
      ? (button.dataset.subscribeText || this.dataset.addToCartText)
      : (tier?.sameProductBogoEligible || (!tier && this.refs.sameProductPromo && !this.refs.sameProductPromo.hidden)
        ? (this.dataset.sameProductAddToCartText || this.dataset.addToCartText)
        : this.dataset.addToCartText);

    button.textContent = text;

    if (this.refs.stickyAddToCartButton) {
      this.refs.stickyAddToCartButton.textContent = text;
    }
  }

  #setProductIdentity(selection) {
    if (!selection) return;

    const title = selection.productTitle || this.dataset.productTitle || '';
    if (!title) return;

    if (this.refs.productTitle) {
      this.refs.productTitle.textContent = title;
    }

    if (this.refs.stickyTitle) {
      this.refs.stickyTitle.textContent = title;
    }

    document.title = this.#documentTitleSuffix
      ? `${title}${this.#documentTitleSuffix}`
      : title;
  }

  #syncVariantPickerStateClasses() {
    if (!this.refs.variantPicker) return;

    const labels = this.refs.variantPicker.querySelectorAll('.variant-option__button-label');
    labels.forEach((label) => {
      const input = label.querySelector('input[type="radio"]');
      label.classList.toggle('variant-option__button-label--selected', !!input && input.checked);
    });
  }

  #captureDocumentTitlePattern() {
    if (this.#originalDocumentTitle || !this.dataset.productTitle) return;

    this.#originalDocumentTitle = document.title;
    if (document.title.startsWith(this.dataset.productTitle)) {
      this.#documentTitleSuffix = document.title.slice(this.dataset.productTitle.length);
    }
  }

  #setStickyPrice(price) {
    if (!this.refs.stickyPrice || !Number.isFinite(price) || price <= 0) return;
    this.refs.stickyPrice.textContent = formatMoneyCents(price);
  }

  #updateStickyAtcVisibility() {
    const sticky = this.refs.stickyAtc;
    const button = this.refs.addToCartButton;
    if (!sticky || !button) return;

    const canShow = this.#mobileStickyMedia.matches && !button.disabled && !this.refs.buyRow?.hidden;
    if (!canShow) {
      sticky.hidden = true;
      return;
    }

    const rect = button.getBoundingClientRect();
    const buttonVisible = rect.top < window.innerHeight && rect.bottom > 0;
    sticky.hidden = buttonVisible;
  }

  #updateBackInStockSelectionLabel() {
    const label = this.refs.backInStockSelection;
    const selection = this.#backInStockSelection;
    if (!label || !selection) return;

    const variantTitle = selection.variantTitle || '';
    const shouldShowVariant = variantTitle && variantTitle !== 'Default Title';
    label.textContent = shouldShowVariant ? variantTitle : '';
    label.hidden = !shouldShowVariant;
  }

  async handleBackInStockSubmit(event) {
    event.preventDefault();

    const form = this.refs.backInStockForm;
    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    const functionUrl = this.dataset.backInStockUrl;
    if (!functionUrl) {
      this.#setBackInStockStatus(this.dataset.backInStockErrorText, 'error');
      return;
    }

    const selection = this.#backInStockSelection || this.#buildSelfBackInStockSelection();
    const submitButton = this.refs.backInStockSubmit;
    const defaultText = submitButton.dataset.defaultText || submitButton.textContent;

    submitButton.disabled = true;
    submitButton.textContent = this.dataset.backInStockSubmittingText || defaultText;
    this.#setBackInStockStatus('', '');

    try {
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: this.refs.backInStockEmail.value.trim(),
          customer_id: this.dataset.customerId || '',
          newsletter: this.refs.backInStockConsent.checked,
          product: {
            id: selection.productId,
            handle: selection.productHandle,
            title: selection.productTitle,
            url: selection.productUrl,
          },
          variant: {
            id: selection.variantId,
            title: selection.variantTitle,
            sku: selection.variantSku,
          },
          source: 'capillaire-pdp',
        }),
      });

      const result = await response.json().catch(() => ({}));

      if (!response.ok || result.error) {
        throw new Error(result.error || 'Back in stock request failed');
      }

      this.#setBackInStockStatus(this.dataset.backInStockSuccessText, 'success');
    } catch (error) {
      console.error('Capillaire PDP: back in stock request failed', error);
      this.#setBackInStockStatus(this.dataset.backInStockErrorText, 'error');
    } finally {
      submitButton.disabled = false;
      submitButton.textContent = defaultText;
    }
  }

  #setBackInStockStatus(message, type) {
    const status = this.refs.backInStockStatus;
    if (!status) return;

    status.textContent = message || '';
    status.hidden = !message;
    status.classList.toggle('capillaire-pdp__back-in-stock-message--success', type === 'success');
    status.classList.toggle('capillaire-pdp__back-in-stock-message--error', type === 'error');
  }

  #navigateToVariantMedia(mediaId) {
    const slideshow = this.querySelector('slideshow-component');
    if (!slideshow) return;
    const slides = slideshow.querySelectorAll('slideshow-slide');
    const idx = [...slides].findIndex((s) => s.getAttribute('slide-id') === String(mediaId));
    if (idx !== -1 && typeof slideshow.select === 'function') {
      slideshow.select(idx);
    }
  }

  /* ── Quantity ── */

  increaseQty() {
    const input = this.refs.quantityInput;
    if (!input) return;
    const max = parseInt(input.max, 10) || 99;
    const current = parseInt(input.value, 10) || 1;
    if (current < max) input.value = current + 1;
  }

  decreaseQty() {
    const input = this.refs.quantityInput;
    if (!input) return;
    const min = parseInt(input.min, 10) || 1;
    const current = parseInt(input.value, 10) || 1;
    if (current > min) input.value = current - 1;
  }

  /* ── Add to cart ── */

  async handleAddToCart(event) {
    event.preventDefault();
    await this.#submitAddToCart(this.refs.addToCartButton);
  }

  async handleStickyAddToCart(event) {
    event.preventDefault();
    await this.#submitAddToCart(this.refs.stickyAddToCartButton || this.refs.addToCartButton);
  }

  async #submitAddToCart(button) {
    const isSubscription = this.refs.subscriptionCheckbox?.checked;
    const tier = this.tiers[this.selectedTierIndex];
    const sellingPlan = isSubscription && tier ? (tier.sellingPlanId || '') : '';
    const variantId = this.refs.variantIdInput.value;
    const quantity = parseInt(this.refs.quantityInput?.value, 10) || 1;
    const formData = new FormData();

    formData.append('items[0][id]', variantId);
    formData.append('items[0][quantity]', quantity.toString());
    if (sellingPlan) formData.append('items[0][selling_plan]', sellingPlan);

    const freeGiftIds = tier?.freeGiftVariantIds?.length
      ? tier.freeGiftVariantIds
      : (this.dataset.freeGiftVariantIds || '').split(',').filter(Boolean);

    let nextItemIndex = 1;

    const shouldAddSameProductGift =
      !sellingPlan &&
      (tier?.sameProductBogoEligible || (!tier && this.refs.sameProductPromo && !this.refs.sameProductPromo.hidden));

    if (shouldAddSameProductGift) {
      formData.append(`items[${nextItemIndex}][id]`, variantId);
      formData.append(`items[${nextItemIndex}][quantity]`, quantity.toString());
      formData.append(`items[${nextItemIndex}][properties][_lv_bogo_same_product_gift]`, 'true');
      formData.append(`items[${nextItemIndex}][properties][_free_gift]`, 'true');
      formData.append(`items[${nextItemIndex}][properties][_lv_bogo_same_product_source_variant_id]`, variantId);
      formData.append(`items[${nextItemIndex}][properties][_lv_promo_type]`, 'same_product_bogo');
      formData.append(`items[${nextItemIndex}][properties][_lv_promo_key]`, `same-product-bogo:${variantId}`);
      nextItemIndex += 1;
    }

    freeGiftIds.forEach((giftId) => {
      const idx = nextItemIndex;
      formData.append(`items[${idx}][id]`, giftId);
      formData.append(`items[${idx}][quantity]`, '1');
      formData.append(`items[${idx}][properties][_free_gift]`, 'true');
      formData.append(`items[${idx}][properties][_lv_free_gift_source_variant_id]`, variantId);
      formData.append(`items[${idx}][properties][_lv_promo_type]`, 'free_gift');
      formData.append(`items[${idx}][properties][_lv_promo_key]`, `free-gift:${variantId}:${giftId}`);
      nextItemIndex += 1;
    });

    await addToCart({
      formData,
      button,
      errorContainer: this.refs.addToCartError,
      liveRegion: this.refs.liveRegion,
      addToCartText: this.refs.addToCartButton.textContent || this.dataset.addToCartText,
      addingText: this.dataset.addingText || '...',
      addedText: this.dataset.addedText || '',
      successClass: 'capillaire-pdp__btn--success',
    });
  }
}

customElements.define('capillaire-pdp', CapillairePdp);
